// <generated-client-wrappers:imports>
import type * as WrapperApi from '../generated/model/models';
import * as WrapperCore from '@angular/core';
import type * as WrapperHttp from '@angular/common/http';
import type * as WrapperRx from 'rxjs';
import * as WrapperServices from '../generated/api/integrationOperations.service';
// </generated-client-wrappers:imports>
import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { REQUEST_META_DATA } from '@seech-sub/core-ng';
import { Observable } from 'rxjs';
import { automationDriftHttpOptions, AutomationDriftRequestOptions } from './automation-drift-request-options';
import { OperationsApiRoutes } from '../constants/operations.constants';
import {
  CreateIntegrationRequestPayload,
  IntegrationConnectionSummary,
  IntegrationDefinitionSummary,
  IntegrationPlatformCredentialSummary,
  IntegrationQuery,
  IntegrationRequestSummary,
  PagedResult,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class IntegrationOperationsClientService {
  private readonly quietContext = new HttpContext().set(REQUEST_META_DATA.HIDE_PROGRESS, true);
  private readonly http = inject(HttpClient);

  queryConnections(query: IntegrationQuery): Observable<PagedResult<IntegrationConnectionSummary>> {
    return this.http.post<PagedResult<IntegrationConnectionSummary>>(OperationsApiRoutes.integrationConnectionsQuery, query, {
      context: this.quietContext,
    });
  }

  getConnection(id: string): Observable<IntegrationConnectionSummary> {
    return this.http.get<IntegrationConnectionSummary>(OperationsApiRoutes.integrationConnectionById(id));
  }

  testConnection(id: string): Observable<void> {
    return this.http.post<void>(OperationsApiRoutes.integrationConnectionTest, { id });
  }

  disconnectConnection(id: string, reason: string): Observable<void> {
    return this.http.post<void>(OperationsApiRoutes.integrationConnectionDisconnect, { id, reason });
  }

  queryDefinitions(query: IntegrationQuery): Observable<PagedResult<IntegrationDefinitionSummary>> {
    return this.http.post<PagedResult<IntegrationDefinitionSummary>>(OperationsApiRoutes.integrationDefinitionsQuery, query, {
      context: this.quietContext,
    });
  }

  publishDefinition(id: string, options?: AutomationDriftRequestOptions): Observable<void> {
    return this.http.post<void>(OperationsApiRoutes.integrationDefinitionPublish(id), {}, automationDriftHttpOptions(options));
  }

  approveDefinition(id: string, note?: string, options?: AutomationDriftRequestOptions): Observable<void> {
    return this.http.post<void>(OperationsApiRoutes.integrationDefinitionApproveReview(id), { note }, automationDriftHttpOptions(options));
  }

  queryPlatformCredentials(query: IntegrationQuery): Observable<PagedResult<IntegrationPlatformCredentialSummary>> {
    return this.http.post<PagedResult<IntegrationPlatformCredentialSummary>>(
      OperationsApiRoutes.integrationPlatformCredentialsQuery,
      query,
      { context: this.quietContext }
    );
  }

  activatePlatformCredential(id: string, options?: AutomationDriftRequestOptions): Observable<void> {
    return this.http.post<void>(OperationsApiRoutes.integrationPlatformCredentialActivate(id), {}, automationDriftHttpOptions(options));
  }

  deactivatePlatformCredential(id: string, reason: string, options?: AutomationDriftRequestOptions): Observable<void> {
    return this.http.post<void>(OperationsApiRoutes.integrationPlatformCredentialDeactivate(id), { reason }, automationDriftHttpOptions(options));
  }

  rotatePlatformCredential(id: string, options?: AutomationDriftRequestOptions): Observable<void> {
    return this.http.post<void>(OperationsApiRoutes.integrationPlatformCredentialRotate(id), {}, automationDriftHttpOptions(options));
  }

  queryRequests(query: IntegrationQuery): Observable<PagedResult<IntegrationRequestSummary>> {
    return this.http.post<PagedResult<IntegrationRequestSummary>>(OperationsApiRoutes.integrationRequestsQuery, query, {
      context: this.quietContext,
    });
  }

  createRequest(payload: CreateIntegrationRequestPayload): Observable<IntegrationRequestSummary> {
    return this.http.post<IntegrationRequestSummary>(OperationsApiRoutes.integrationRequests, payload);
  }

  requeueRequest(id: string, reason: string): Observable<void> {
    return this.http.post<void>(OperationsApiRoutes.integrationRequestRequeue(id), { reason });
  }
  // <generated-client-wrappers:methods>
  private readonly wrapperGeneratedClient = WrapperCore.inject(
    WrapperServices.IntegrationOperationsService,
    { optional: true },
  );

  approveDefinitionReview(
    id: string,
    request: WrapperApi.ApproveIntegrationDefinitionReviewRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsApproveDefinitionReview(
      id,
      request,
      'body',
      reportProgress,
      options,
    );
  }

  createDefinition(
    request: WrapperApi.IntegrationDefinitionUpsertRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsCreateDefinition(
      request,
      'body',
      reportProgress,
      options,
    );
  }

  deleteDefinition(
    id: string,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsDeleteDefinition(
      id,
      'body',
      reportProgress,
      options,
    );
  }

  getDefinition(
    id: string,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: 'text/plain' | 'application/json' | 'text/json';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<WrapperApi.IntegrationDefinitionDetail> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsGetDefinition(
      id,
      'body',
      reportProgress,
      options,
    );
  }

  getPlatformCredential(
    id: string,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: 'text/plain' | 'application/json' | 'text/json';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<WrapperApi.IntegrationPlatformCredentialDetail> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsGetPlatformCredential(
      id,
      'body',
      reportProgress,
      options,
    );
  }

  getRequest(
    id: string,
    appSegmentId?: string,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: 'text/plain' | 'application/json' | 'text/json';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<WrapperApi.IntegrationRequestDetail> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsGetRequest(
      id,
      appSegmentId,
      'body',
      reportProgress,
      options,
    );
  }

  listDefinitionVersions(
    id: string,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: 'text/plain' | 'application/json' | 'text/json';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<
    Array<WrapperApi.IntegrationDefinitionVersionResponse>
  > {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsListDefinitionVersions(
      id,
      'body',
      reportProgress,
      options,
    );
  }

  materializeRequest(
    id: string,
    request: WrapperApi.MaterializeIntegrationRequestDefinitionRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsMaterializeRequest(
      id,
      request,
      'body',
      reportProgress,
      options,
    );
  }

  queryCatalog(
    request: WrapperApi.IntegrationCatalogQuery,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: 'text/plain' | 'application/json' | 'text/json';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<WrapperApi.IntegrationCatalogPage> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsQueryCatalog(
      request,
      'body',
      reportProgress,
      options,
    );
  }

  queryDefinitionReviews(
    request: WrapperApi.IntegrationDefinitionReviewQueueRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: 'text/plain' | 'application/json' | 'text/json';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<WrapperApi.IntegrationDefinitionReviewQueuePage> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsQueryDefinitionReviews(
      request,
      'body',
      reportProgress,
      options,
    );
  }

  queryUsage(
    request: WrapperApi.IntegrationUsageStatsQuery,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: 'text/plain' | 'application/json' | 'text/json';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<WrapperApi.IntegrationUsageStatsPage> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsQueryUsage(
      request,
      'body',
      reportProgress,
      options,
    );
  }

  runDuePlatformCredentialRotation(
    request: WrapperApi.RunDueIntegrationPlatformCredentialRotationRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsRunDuePlatformCredentialRotation(
      request,
      'body',
      reportProgress,
      options,
    );
  }

  submitDefinitionForReview(
    id: string,
    request: WrapperApi.SubmitIntegrationDefinitionReviewRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsSubmitDefinitionForReview(
      id,
      request,
      'body',
      reportProgress,
      options,
    );
  }

  updateDefinition(
    id: string,
    request: WrapperApi.IntegrationDefinitionUpsertRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsUpdateDefinition(
      id,
      request,
      'body',
      reportProgress,
      options,
    );
  }

  upsertPlatformCredential(
    request: WrapperApi.UpsertIntegrationPlatformCredentialRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsUpsertPlatformCredential(
      request,
      'body',
      reportProgress,
      options,
    );
  }

  validateDefinition(
    request: WrapperApi.IntegrationDefinitionUpsertRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('IntegrationOperationsService is not available');
    return this.wrapperGeneratedClient.integrationOperationsValidateDefinition(
      request,
      'body',
      reportProgress,
      options,
    );
  }
  // </generated-client-wrappers:methods>
}
